/**
 * Step 3: Model Structure
 */
import React, { useEffect } from 'react';
import { Form, Select, Input, Table, Button, InputNumber, Checkbox } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const { TextArea } = Input;

interface Step3Props {
  data: any;
  onChange: (data: any) => void;
}

const Step3ModelStructure: React.FC<Step3Props> = ({ data, onChange }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue(data);
  }, [data, form]);

  const handleValuesChange = (_: any, allValues: any) => {
    onChange(allValues);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={handleValuesChange}
      initialValues={{
        ...data,
        administration_modes: data.administration_modes?.length
          ? data.administration_modes
          : ['intermittent'],
        parameters: data.parameters || [],
      }}
    >
      <Form.Item label="房室模型" name="compartment_model">
        <Select placeholder="选择房室模型">
          <Select.Option value="one_compartment">一室模型</Select.Option>
          <Select.Option value="two_compartment">二室模型</Select.Option>
          <Select.Option value="three_compartment">三室模型</Select.Option>
          <Select.Option value="一室模型">一室模型</Select.Option>
          <Select.Option value="二室模型">二室模型</Select.Option>
          <Select.Option value="三室模型">三室模型</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item label="建模软件" name="software">
        <Input placeholder="例如: NONMEM 7.2" />
      </Form.Item>

      <Form.Item
        label="支持给药方式"
        name="administration_modes"
        rules={[{ required: true, message: '请至少选择一种模型实际支持的给药方式' }]}
      >
        <Checkbox.Group
          options={[
            { label: '静脉滴注', value: 'intermittent' },
            { label: '连续输注', value: 'continuous' },
            { label: '口服', value: 'oral' },
            { label: '静脉注射', value: 'bolus' },
          ]}
        />
      </Form.Item>

      <Form.Item label="吸收方式" name="absorption">
        <Select placeholder="选择吸收方式">
          <Select.Option value="none">无（静脉给药）</Select.Option>
          <Select.Option value="first_order">一级吸收</Select.Option>
          <Select.Option value="zero_order">零级吸收/恒速输入</Select.Option>
          <Select.Option value="null">无（静脉给药）</Select.Option>
          <Select.Option value="一级吸收">一级吸收</Select.Option>
          <Select.Option value="零级吸收">零级吸收（恒速输入/控释/透皮）</Select.Option>
          <Select.Option value="一级吸收+滞后时间">一级吸收 + 滞后时间</Select.Option>
          <Select.Option value="转运室吸收">转运室吸收</Select.Option>
          <Select.Option value="混合吸收（串联/并联）">混合吸收（串联/并联）</Select.Option>
          <Select.Option value="Weibull吸收">Weibull/时间变异吸收</Select.Option>
          <Select.Option value="饱和/非线性吸收">饱和/非线性吸收</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item label="消除方式" name="elimination">
        <Select placeholder="选择消除方式">
          <Select.Option value="first_order">线性消除（一级）</Select.Option>
          <Select.Option value="zero_order">零级消除</Select.Option>
          <Select.Option value="michaelis_menten">米氏消除</Select.Option>
          <Select.Option value="线性消除">线性消除</Select.Option>
          <Select.Option value="非线性消除">非线性消除（未细分）</Select.Option>
          <Select.Option value="零级消除">零级消除</Select.Option>
          <Select.Option value="米氏消除">米氏消除（Michaelis-Menten）</Select.Option>
          <Select.Option value="混合消除（线性+米氏）">混合消除（线性 + 米氏）</Select.Option>
          <Select.Option value="时间依赖性清除">时间依赖性清除（诱导/抑制）</Select.Option>
          <Select.Option value="靶点介导药物处置（TMDD）">靶点介导药物处置（TMDD）</Select.Option>
          <Select.Option value="转运体介导消除">转运体介导消除</Select.Option>
          <Select.Option value="体外清除（透析/CRRT）">体外清除（透析/CRRT）</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item label="清除率方程" name="clearance_equation">
        <TextArea rows={2} placeholder="例如: CL = 3.65 * (WT/12.8)^0.75" />
      </Form.Item>

      <Form.Item label="分布容积方程" name="volume_equation">
        <TextArea rows={2} placeholder="例如: V = 11.2 * (WT/12.8)" />
      </Form.Item>

      <Form.Item label="模型参数">
        <Form.List name="parameters">
          {(fields, { add, remove }) => (
            <>
              <Table
                dataSource={fields}
                pagination={false}
                size="small"
                columns={[
                  {
                    title: '参数名',
                    dataIndex: 'parameter_name',
                    render: (_, field) => (
                      <Form.Item name={[field.name, 'parameter_name']} noStyle>
                        <Input placeholder="CL" />
                      </Form.Item>
                    ),
                  },
                  {
                    title: '典型值',
                    dataIndex: 'typical_value',
                    render: (_, field) => (
                      <Form.Item name={[field.name, 'typical_value']} noStyle>
                        <InputNumber placeholder="3.65" style={{ width: '100%' }} />
                      </Form.Item>
                    ),
                  },
                  {
                    title: '单位',
                    dataIndex: 'unit',
                    render: (_, field) => (
                      <Form.Item name={[field.name, 'unit']} noStyle>
                        <Input placeholder="L/h" />
                      </Form.Item>
                    ),
                  },
                  {
                    title: 'RSE%',
                    dataIndex: 'rse_percent',
                    render: (_, field) => (
                      <Form.Item name={[field.name, 'rse_percent']} noStyle>
                        <InputNumber placeholder="5.4" style={{ width: '100%' }} />
                      </Form.Item>
                    ),
                  },
                  {
                    title: '描述',
                    dataIndex: 'description',
                    render: (_, field) => (
                      <Form.Item name={[field.name, 'description']} noStyle>
                        <Input placeholder="清除率" />
                      </Form.Item>
                    ),
                  },
                  {
                    title: '操作',
                    render: (_, field) => (
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(field.name)}
                      />
                    ),
                  },
                ]}
              />
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
                style={{ marginTop: 8 }}
              >
                添加参数
              </Button>
            </>
          )}
        </Form.List>
      </Form.Item>
    </Form>
  );
};

export default Step3ModelStructure;
